import dayjs from 'dayjs';
import { useMemo } from 'react';

import { t } from '../../../services/translation/translation';
import {
  endOfMonth,
  formatDate,
  getDate,
  getDayOfWeek,
  getMonth,
  getYear,
  parseDate,
  startOfMonth,
  subtractDays,
  subtractMonths,
} from '../../../utils/date';
import { Cell } from '../Cell';
import { Controls } from '../Controls';

type Props = {
  selectedDay?: number;
  displayYear: number;
  displayMonth: number;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onSelectDay: (_day: number) => void;
  onHeaderClick: () => void;
  maxDate?: number;
  minDate?: number;
};

export const DaysView = ({
  onHeaderClick,
  onNextMonth,
  onPreviousMonth,
  onSelectDay,
  selectedDay,
  displayMonth,
  displayYear,
  maxDate,
  minDate,
}: Props) => {
  const { maxYear, minYear, maxMonth, minMonth, maxDay, minDay } = useMemo(() => {
    const maxDay = maxDate ? getDate(maxDate) : undefined;
    const minDay = minDate ? getDate(minDate) : undefined;
    const maxYear = maxDate ? getYear(maxDate) : undefined;
    const minYear = minDate ? getYear(minDate) : undefined;
    const maxMonth = maxDate ? getMonth(maxDate) : undefined;
    const minMonth = minDate ? getMonth(minDate) : undefined;

    return { maxYear, minYear, maxMonth, minMonth, maxDay, minDay };
  }, [maxDate, minDate]);

  const { daysBefore, days, daysAfter } = useMemo(() => {
    const selectedMonth = new Date(displayYear, displayMonth, 15).valueOf();
    const start = startOfMonth(selectedMonth);
    const end = endOfMonth(selectedMonth);
    const nbDays = getDate(end);
    const nbDaysBefore = getDayOfWeek(start);
    const nbDaysAfter = 42 - nbDaysBefore - nbDays;

    return {
      daysBefore: Array.from({ length: nbDaysBefore })
        .map((_, index) => {
          return getDate(subtractDays(endOfMonth(subtractMonths(selectedMonth, 1)), index));
        })
        .reverse(),
      days: Array.from({ length: nbDays }).map((_, index) => index + 1),
      daysAfter: Array.from({ length: nbDaysAfter }).map((_, index) => index + 1),
    };
  }, [displayMonth, displayYear]);

  return (
    <div className="inline-block">
      <Controls
        onHeaderClick={onHeaderClick}
        onPrevious={onPreviousMonth}
        onNext={onNextMonth}
        label={formatDate(parseDate(`${displayYear}-${displayMonth + 1}-15`, 'YYYY-M-D'), t('date.month-year'))}
        isNextDisabled={
          maxYear != null &&
          maxMonth != null &&
          (displayYear > maxYear || (displayYear === maxYear && displayMonth >= maxMonth))
        }
        isPreviousDisabled={
          minYear != null &&
          minMonth != null &&
          (displayYear < minYear || (displayYear === minYear && displayMonth <= minMonth))
        }
      />

      <div>
        <div className="grid grid-cols-7 mb-1">
          {Array.from({ length: 7 }).map((_, index) => (
            <span
              key={index}
              className="text-center h-6 leading-6 text-sm font-medium capitalize text-gray-500 dark:text-gray-400"
            >
              {dayjs().weekday(index).format('dd')}
            </span>
          ))}
        </div>
        <div className="w-64 grid grid-cols-7">
          {daysBefore.map(day => (
            <Cell key={day} isDisabled>
              {day}
            </Cell>
          ))}
          {days.map(day => (
            <Cell
              key={day}
              isSelected={selectedDay === day}
              onClick={() => onSelectDay(day)}
              isDisabled={
                (maxYear != null &&
                  maxMonth != null &&
                  maxDay != null &&
                  (displayYear > maxYear ||
                    (displayYear === maxYear && displayMonth > maxMonth) ||
                    (displayYear === maxYear && displayMonth === maxMonth && day > maxDay))) ||
                (minYear != null &&
                  minMonth != null &&
                  minDay != null &&
                  (displayYear < minYear ||
                    (displayYear === minYear && displayMonth < minMonth) ||
                    (displayYear === minYear && displayMonth === minMonth && day < minDay)))
              }
            >
              {day}
            </Cell>
          ))}
          {daysAfter.map((day, index) => (
            <Cell key={index} isDisabled>
              {day}
            </Cell>
          ))}
        </div>
      </div>
    </div>
  );
};
